<script setup>
import { ref, computed } from 'vue';
import { transchoice } from '@elements/translation';
import PaginationButton from './PaginationButton.vue';
import PaginationShortcuts from './PaginationShortcuts.vue';

const props = defineProps({
  offset: { type: Number, default: 0 },
  nbItems: { type: Number, default: 0 },
  page: { type: Number, default: 0 },
  nbPages: { type: Number, default: 0 },
});

const emit = defineEmits([ 'go-page' ]);

const currentPage = ref(props.page);

const hasPreviousPage = computed(() => currentPage.value - 1 > 0);
const hasNextPage = computed(() => currentPage.value + 1 <= props.nbPages);

const offsetStart = computed(() => (currentPage.value - 1) * props.offset + 1);

const offsetEnd = computed(() => {
  const lastValue = offsetStart.value + props.offset - 1;
  return lastValue < props.nbItems ? lastValue : props.nbItems;
});

const pagesShortcuts = computed(() => {
  const shortcuts = [];

  if (currentPage.value >= 4) {
    shortcuts.push(1);
  }

  for (let i = 10; i <= 100; i += 10) {
    if (i > props.nbPages) {
      break;
    }
    shortcuts.push(i);
  }

  return shortcuts;
});

const goToPage = (page) => {
  if (currentPage.value === page) {
    return;
  }

  currentPage.value = page;
  emit('go-page', page);
};

const goNextPage = () => goToPage(currentPage.value + 1);
const goPreviousPage = () => goToPage(currentPage.value - 1);
</script>

<template>
  <div class="pagination-short">
    <p class="pagination-short__summary">
      {{ transchoice('global.pagination.summary', {
        offset_start: offsetStart,
        offset_end: offsetEnd,
        nb_items: nbItems
      }) }}
    </p>

    <div class="pagination-short__buttons">
      <PaginationButton
        v-if="hasPreviousPage"
        variant="previous"
        @click.prevent="goPreviousPage"
      >
        <span class="icon" />
      </PaginationButton>

      <PaginationButton
        v-for="pageItem in nbPages"
        :key="pageItem"
        :class="{ selected: pageItem === currentPage }"
        :disabled="pageItem === currentPage"
        @click.prevent="() => goToPage(pageItem)"
      >
        {{ pageItem }}
      </PaginationButton>

      <PaginationButton
        v-if="hasNextPage"
        variant="next"
        @click.prevent="goNextPage"
      >
        <span class="icon" />
      </PaginationButton>
    </div>

    <PaginationShortcuts
      v-if="pagesShortcuts.length"
      :shortcuts="pagesShortcuts"
    />
  </div>
</template>
